import { useState } from "react";
import { Link } from "react-router";
import { vapidPaar } from "../push";
import { Feld } from "../ui/Feld";
import { Karte } from "../ui/Karte";
import { Knopf } from "../ui/Knopf";
import { NeueAufgabeKnopf } from "../ui/NeueAufgabeKnopf";
import { useHinweis } from "../ui/Hinweise";
import ui from "../ui/ui.module.css";
import s from "./seiten.module.css";

/** Nur Admins: die drei Werte für Push (VAPID) im Browser erzeugen, ohne Terminal (BAUPLAN-ABSCHLUSS.md, Abschnitt 6).
 *  Die Werte stehen nur auf dieser Seite, bis sie geschlossen wird. Nichts wird gespeichert, gesendet oder geloggt. */
export function PushSchluessel() {
  const { zeigen } = useHinweis();
  const [paar, setPaar] = useState<{ oeffentlich: string; privat: string } | null>(null);
  const [adresse, setAdresse] = useState("");
  const subject = adresse.trim() ? `mailto:${adresse.trim()}` : "";

  async function erzeugen() {
    try {
      setPaar(await vapidPaar());
    } catch {
      zeigen("Dieser Browser kann die Schlüssel nicht erzeugen. Versuch es mit einem aktuellen Chrome, Firefox oder Safari.", { fehler: true });
    }
  }

  async function kopieren(wert: string, name: string) {
    try {
      await navigator.clipboard.writeText(wert);
      zeigen(`${name} kopiert. Jetzt in Netlify einfügen.`);
    } catch {
      zeigen("Kopieren ging nicht. Markier den Wert und kopier ihn selbst.", { fehler: true });
    }
  }

  const zeile = (name: string, wert: string, geheim: boolean) => (
    <div className={s.schluesselZeile}>
      <label className={ui.feld}>
        <span className={ui.label}>
          {name}
          {geheim ? " (geheim)" : ""}
        </span>
        <input className={[ui.eingabe, s.schluesselWert].join(" ")} readOnly value={wert} placeholder="Noch nicht erzeugt" spellCheck={false} autoComplete="off" onFocus={(e) => e.target.select()} />
      </label>
      <Knopf disabled={!wert} onClick={() => void kopieren(wert, name)}>
        Kopieren<span className="nur-leser"> {name}</span>
      </Knopf>
    </div>
  );

  return (
    <div className={s.stapel}>
      <div className={s.kopf}>
        <div>
          <Link to="/mitteilungen" className={s.textknopf}>
            ← Mitteilungen
          </Link>
          <h1>Push-Schlüssel erzeugen.</h1>
          <p>Die drei Werte für Push trägst du einmal in Netlify ein. Erzeugt wird hier im Browser. Nichts davon wird gespeichert oder verschickt.</p>
        </div>
        <NeueAufgabeKnopf />
      </div>
      <Karte titel="1. Erzeugen">
        <div className={s.stapel}>
          <Feld
            label="Adresse für Push-Dienste"
            type="email"
            autoComplete="off"
            value={adresse}
            onChange={(e) => setAdresse(e.target.value)}
            hilfe="Eine Adresse des Vereins, unter der Apple, Google oder Mozilla bei Problemen schreiben können, zum Beispiel die aus dem Impressum."
          />
          <div>
            <Knopf art="primaer" onClick={() => void erzeugen()}>
              {paar ? "Neu erzeugen" : "Schlüsselpaar erzeugen"}
            </Knopf>
          </div>
          {zeile("VAPID_PUBLIC_KEY", paar?.oeffentlich ?? "", false)}
          {zeile("VAPID_PRIVATE_KEY", paar?.privat ?? "", true)}
          {zeile("VAPID_SUBJECT", subject, false)}
        </div>
      </Karte>
      <Karte titel="2. In Netlify eintragen">
        <ol className={s.schritte}>
          <li>Netlify, Projekt obsn-app, Project configuration, Environment variables, „Add a variable“.</li>
          <li>Je Wert eine Variable mit dem Namen links, Kontext Production, Scope Functions. Bei VAPID_PRIVATE_KEY „Contains secret values“ an.</li>
          <li>Deploys, „Trigger deploy“. Danach diese Seite schließen.</li>
          <li>Im Profil „Push einschalten“ und „Test-Push“.</li>
        </ol>
        <p className={ui.hilfe}>
          Nur einmal anlegen. Wer die Werte später ändert, macht alle eingerichteten Geräte ungültig: Dann muss jede Person Push im Profil neu einschalten.
        </p>
      </Karte>
    </div>
  );
}
